import { useParams, Link, useNavigate } from "react-router-dom";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { PageBreadcrumb } from "@jambushrushti/components/ui/page-breadcrumb";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { useGuruvaniById } from "@jambushrushti/hooks/useGuruvani";
import { useGuruvaniFromDB } from "@jambushrushti/hooks/useContent";
import { SocialShareButtons } from "@jambushrushti/components/social/SocialShareButtons";
import {
  ArrowLeft,
  BookOpen,
  Mic,
  FileText,
  Lock,
  Calendar,
  Globe,
  Hash,
  Quote,
  ArrowRight,
  Loader2,
} from "lucide-react";

const typeIcons: Record<string, typeof Mic> = {
  Discourse: Mic,
  "Written Work": BookOpen,
  Letter: FileText,
  Notes: FileText,
};

const GuruvaniDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: item, isLoading } = useGuruvaniById(id || "");
  const { data: allItems = [] } = useGuruvaniFromDB();

  // Get related items (same category, excluding current)
  const relatedItems = item
    ? allItems
        .filter(
          i =>
            i.category === item.category && i.id !== item.id && !i.is_restricted
        )
        .slice(0, 3)
    : [];

  if (isLoading) {
    return (
      <Layout>
        <section className="bg-background py-20">
          <div className="container mx-auto px-4 text-center">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
            <p className="mt-4 text-muted-foreground">Loading content...</p>
          </div>
        </section>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <section className="bg-background py-20">
          <div className="container mx-auto px-4 text-center">
            <Mic className="mx-auto mb-4 h-16 w-16 text-muted-foreground/30" />
            <h1 className="font-heading mb-4 text-2xl font-bold text-foreground">
              Content Not Found
            </h1>
            <p className="mb-6 text-muted-foreground">
              The Guruvani content you're looking for doesn't exist or has been
              removed.
            </p>
            <Button variant="hero" asChild>
              <Link to="/guruvani">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Guruvani
              </Link>
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  const Icon = typeIcons[item.category || ""] || FileText;

  return (
    <Layout>
      {/* Breadcrumb */}
      <PageBreadcrumb
        items={[
          { label: "Guruvani", href: "/guruvani" },
          { label: item.title },
        ]}
      />

      {/* Hero with Quote */}
      <section className="bg-burgundy/5 border-burgundy/10 border-b py-8">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <Quote className="text-burgundy/30 mx-auto mb-3 h-8 w-8" />
            <blockquote className="font-heading text-lg italic text-foreground">
              "The lamp of knowledge dispels the darkness of ignorance."
            </blockquote>
          </div>
        </div>
      </section>

      {/* Content Details */}
      <section className="bg-background py-12 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl">
            {/* Header */}
            <div className="animate-fade-up mb-8">
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="bg-burgundy/10 flex h-12 w-12 items-center justify-center rounded-xl">
                  <Icon className="text-burgundy h-6 w-6" />
                </div>
                <Badge variant="secondary">{item.category || "Teaching"}</Badge>
                <Badge variant="outline">
                  {item.source || "Source not specified"}
                </Badge>
                {item.is_restricted && (
                  <Badge variant="destructive" className="gap-1">
                    <Lock className="h-3 w-3" />
                    Restricted
                  </Badge>
                )}
              </div>

              <h1 className="font-heading mb-4 text-3xl font-bold text-foreground lg:text-4xl">
                {item.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                {item.date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>{new Date(item.date).getFullYear()}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  <span>{item.source || "Source not specified"}</span>
                </div>
              </div>
            </div>

            {/* Image if available */}
            {item.image_url && (
              <div className="animate-fade-up mb-8 overflow-hidden rounded-2xl delay-100">
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="h-auto max-h-[400px] w-full object-cover"
                />
              </div>
            )}

            {/* Content */}
            <div className="animate-fade-up delay-200">
              {item.is_restricted ? (
                <div className="rounded-2xl border border-border bg-secondary/50 p-8 text-center">
                  <Lock className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
                  <h3 className="font-heading mb-2 text-xl font-bold text-foreground">
                    Restricted Content
                  </h3>
                  <p className="mx-auto mb-6 max-w-md text-muted-foreground">
                    This content requires authentication to access.
                  </p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <Button variant="hero" asChild>
                      <Link to="/auth">Sign In to Access</Link>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="prose prose-lg max-w-none">
                  <div className="rounded-2xl border border-border bg-secondary/30 p-8">
                    {(item.content || "")
                      .split("\n\n")
                      .map((paragraph, index) => (
                        <p
                          key={index}
                          className="font-body mb-4 text-base leading-relaxed text-foreground last:mb-0"
                        >
                          {paragraph}
                        </p>
                      ))}
                    {!item.content && (
                      <p className="italic text-muted-foreground">
                        No content available for this item.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Audio/Video if available */}
            {(item.audio_url || item.video_url) && !item.is_restricted && (
              <div className="animate-fade-up mt-8 delay-300">
                {item.video_url && (
                  <div className="overflow-hidden rounded-2xl">
                    <video
                      controls
                      className="w-full"
                      poster={item.image_url || undefined}
                    >
                      <source src={item.video_url} />
                    </video>
                  </div>
                )}
                {item.audio_url && !item.video_url && (
                  <audio controls className="w-full">
                    <source src={item.audio_url} />
                  </audio>
                )}
              </div>
            )}

            {/* Share Buttons */}
            {!item.is_restricted && (
              <div className="animate-fade-up mt-8 border-t border-border pt-6 delay-300">
                <SocialShareButtons
                  title={item.title}
                  description={item.content?.substring(0, 200) || ""}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related Content */}
      {relatedItems.length > 0 && (
        <section className="bg-gradient-spiritual relative overflow-hidden py-16">
          <div className="lotus-pattern absolute inset-0 opacity-30" />

          <div className="container relative z-10 mx-auto px-4">
            <div className="mb-10 text-center">
              <span className="text-burgundy text-xs font-medium uppercase tracking-widest">
                Continue Reading
              </span>
              <h2 className="font-heading mt-2 text-2xl font-bold text-foreground lg:text-3xl">
                Related Teachings
              </h2>
            </div>

            <div className="mx-auto grid max-w-4xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {relatedItems.map((relatedItem, index) => {
                const RelatedIcon =
                  typeIcons[relatedItem.category || ""] || FileText;
                return (
                  <Link
                    key={relatedItem.id}
                    to={`/guruvani/${relatedItem.id}`}
                    className="group"
                  >
                    <Card
                      variant="interactive"
                      className="animate-fade-up h-full overflow-hidden bg-background/80 backdrop-blur-sm"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <CardContent className="p-6">
                        <div className="mb-3 flex items-center gap-3">
                          <div className="bg-burgundy/10 flex h-10 w-10 items-center justify-center rounded-lg">
                            <RelatedIcon className="text-burgundy h-5 w-5" />
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {relatedItem.source || "Source not specified"}
                          </Badge>
                        </div>
                        <h3 className="font-heading mb-2 line-clamp-2 font-medium text-foreground transition-colors group-hover:text-primary">
                          {relatedItem.title}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {relatedItem.category || "Teaching"} •{" "}
                          {relatedItem.date
                            ? new Date(relatedItem.date).getFullYear()
                            : ""}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="border-t border-border bg-background py-12">
        <div className="container mx-auto px-4 text-center">
          <p className="mb-4 text-muted-foreground">
            Explore more sacred teachings from Gurudev
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="spiritual" asChild>
              <Link to="/guruvani">
                <Mic className="mr-2 h-4 w-4" />
                Browse All Guruvani
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/books">
                View Sacred Texts
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default GuruvaniDetails;
